import { useState } from "react";
import { login, forgotPassword, resetPassword, type AuthUser } from "../lib/authClient";

interface Props {
  isLight: boolean;
  onLoggedIn: (user: AuthUser) => void;
  // When present (from a `/?reset=<token>` link), the screen opens straight into "set new
  // password" mode. onResetDone is called after a successful reset to clear the URL token.
  resetToken?: string;
  onResetDone?: () => void;
  // Message to show on open (e.g. "your account is not active") when a suspended user was bounced.
  initialNotice?: string;
}

type Mode = "login" | "forgot" | "reset";
type Notice = { ok: boolean; text: string } | null;

// Full-screen auth gate: sign in, request a reset link, or set a new password (via emailed link).
export function LoginScreen({ onLoggedIn, resetToken, onResetDone, initialNotice }: Props) {
  const [mode, setMode] = useState<Mode>(resetToken ? "reset" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [notice, setNotice] = useState<Notice>(initialNotice ? { ok: false, text: initialNotice } : null);
  const [busy, setBusy] = useState(false);

  // Logged out, the login card is ALWAYS the peach/light palette (dark text) on the colorful mesh
  // background — independent of the app's light/dark theme (there is no theme toggle here).
  const c = { bg: "#f1f5f9", panel: "#fbe6d4", border: "1px solid #f3d6c1", shadow: "0 16px 48px rgba(15,23,42,.18)", text: "#0f172a", sub: "#475569", inputBg: "#ffffff", inputBorder: "1px solid #e7cdb8" };

  // Modern mesh backdrop behind the login card — overlapping ANGULAR color washes (linear, not
  // circular blobs) at scattered angles so no color is pinned to a corner. Mixes orange, blue, red,
  // white, grey, and the light-mode peach tone over a light diagonal base. The card keeps c.panel.
  const bgGradient =
    "linear-gradient(122deg, rgba(249,115,22,.50) 0%, rgba(249,115,22,0) 48%)," +   // orange
    "linear-gradient(58deg, rgba(37,99,235,.46) 0%, rgba(37,99,235,0) 46%)," +      // blue
    "linear-gradient(205deg, rgba(253,205,163,.62) 0%, rgba(253,205,163,0) 52%)," + // peach
    "linear-gradient(340deg, rgba(220,38,38,.42) 0%, rgba(220,38,38,0) 50%)," +     // red
    "linear-gradient(15deg, rgba(148,163,184,.42) 0%, rgba(148,163,184,0) 46%)," +  // grey
    "linear-gradient(255deg, rgba(255,255,255,.55) 0%, rgba(255,255,255,0) 52%)," + // white
    "linear-gradient(95deg, rgba(37,99,235,.34) 0%, rgba(37,99,235,0) 44%)," +      // blue (again)
    "linear-gradient(160deg, rgba(249,115,22,.34) 0%, rgba(249,115,22,0) 44%)," +   // orange (again)
    "linear-gradient(300deg, rgba(148,163,184,.28) 0%, rgba(148,163,184,0) 48%)," + // grey (again)
    "linear-gradient(30deg, rgba(253,205,163,.34) 0%, rgba(253,205,163,0) 46%)," +  // peach (again)
    "linear-gradient(135deg, #f8fafc 0%, #fdf1e6 40%, #eef2f7 72%, #ffffff 100%)";  // base: white / peach / grey

  const inputStyle: React.CSSProperties = { padding: "10px 12px", borderRadius: 6, border: c.inputBorder, background: c.inputBg, color: c.text, fontSize: 13, outline: "none" };
  const linkBtnStyle: React.CSSProperties = { background: "none", border: "none", padding: 0, color: c.sub, fontSize: 12, fontWeight: 600, cursor: "pointer", textDecoration: "underline", fontFamily: "Mulish, sans-serif" };

  function switchMode(next: Mode) { setMode(next); setNotice(null); }

  async function submitLogin(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setNotice(null);
    if (!email.trim() || !password) { setNotice({ ok: false, text: "Enter your email and password." }); return; }
    setBusy(true);
    try {
      const user = await login(email.trim(), password);
      onLoggedIn(user); // screen unmounts on success — leave busy=true
    } catch (err) {
      setNotice({ ok: false, text: err instanceof Error ? err.message : "Login failed." });
      setBusy(false);
    }
  }

  async function submitForgot(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setNotice(null);
    if (!email.trim()) { setNotice({ ok: false, text: "Enter your email." }); return; }
    setBusy(true);
    try {
      await forgotPassword(email.trim());
      // Generic message on purpose — never reveal whether the email has an account.
      setNotice({ ok: true, text: "If an account exists for that email, we've sent a password reset link. Check your inbox (and spam)." });
    } catch (err) {
      setNotice({ ok: false, text: err instanceof Error ? err.message : "Could not send the reset link." });
    } finally {
      setBusy(false);
    }
  }

  async function submitReset(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setNotice(null);
    if (newPassword.length < 8) { setNotice({ ok: false, text: "New password must be at least 8 characters." }); return; }
    if (newPassword !== confirmPassword) { setNotice({ ok: false, text: "The two passwords don't match." }); return; }
    setBusy(true);
    try {
      await resetPassword(resetToken ?? "", newPassword);
      setNotice({ ok: true, text: "Your password has been reset. You can now sign in with your new password." });
      setPassword(""); setNewPassword(""); setConfirmPassword("");
      onResetDone?.();      // strip the ?reset token from the URL
      setMode("login");     // fall back to the sign-in form (success notice stays visible)
    } catch (err) {
      setNotice({ ok: false, text: err instanceof Error ? err.message : "Could not reset your password." });
    } finally {
      setBusy(false);
    }
  }

  const subtitle = mode === "login" ? "Sign in to your account below."
    : mode === "forgot" ? "Enter your account email and we'll send a reset link."
    : "Choose a new password for your account.";

  return (
    <div style={{ position: "fixed", inset: 0, background: bgGradient, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 11000, fontFamily: "Mulish, sans-serif" }}>
      <form
        onSubmit={mode === "login" ? submitLogin : mode === "forgot" ? submitForgot : submitReset}
        style={{ background: c.panel, border: c.border, borderRadius: 12, boxShadow: c.shadow, padding: "32px 30px", width: "92%", maxWidth: 380, display: "flex", flexDirection: "column", gap: 14 }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 2 }}>
          <img src="/lsg-logo.png" alt="Local SEO Garage" style={{ width: 240, maxWidth: "100%", height: "auto", display: "block" }} />
        </div>
        <div style={{ fontSize: 12, color: c.sub, marginTop: 10, marginBottom: 4, textAlign: "center" }}>{subtitle}</div>

        {mode === "login" && (
          <>
            <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 11, color: c.sub, fontWeight: 600 }}>
              EMAIL
              <input type="email" autoFocus value={email} onChange={e => setEmail(e.target.value)} autoComplete="username" style={inputStyle} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 11, color: c.sub, fontWeight: 600 }}>
              PASSWORD
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" style={inputStyle} />
            </label>
            <button type="button" onClick={() => switchMode("forgot")} style={{ ...linkBtnStyle, alignSelf: "flex-start", marginTop: -4 }}>Forgot password?</button>
          </>
        )}

        {mode === "forgot" && (
          <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 11, color: c.sub, fontWeight: 600 }}>
            EMAIL
            <input type="email" autoFocus value={email} onChange={e => setEmail(e.target.value)} autoComplete="username" style={inputStyle} />
          </label>
        )}

        {mode === "reset" && (
          <>
            <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 11, color: c.sub, fontWeight: 600 }}>
              NEW PASSWORD
              <input type="password" autoFocus value={newPassword} onChange={e => setNewPassword(e.target.value)} autoComplete="new-password" placeholder="At least 8 characters" style={inputStyle} />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 11, color: c.sub, fontWeight: 600 }}>
              CONFIRM NEW PASSWORD
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} autoComplete="new-password" style={inputStyle} />
            </label>
          </>
        )}

        {notice && <div style={{ fontSize: 12, color: notice.ok ? "#16a34a" : "#ef4444", lineHeight: 1.45 }}>{notice.text}</div>}

        <button type="submit" disabled={busy}
          style={{ marginTop: 4, alignSelf: "center", width: "50%", padding: "11px 18px", borderRadius: 8, border: "none", background: busy ? "#475569" : "#16a34a", color: "#fff", fontFamily: "Mulish, sans-serif", fontSize: 13, fontWeight: 700, cursor: busy ? "not-allowed" : "pointer" }}>
          {mode === "login" ? (busy ? "Signing in…" : "Sign In")
            : mode === "forgot" ? (busy ? "Sending…" : "Send Reset Link")
            : (busy ? "Resetting…" : "Reset Password")}
        </button>

        {mode !== "login" && (
          <button type="button" onClick={() => switchMode("login")} style={{ ...linkBtnStyle, alignSelf: "center" }}>← Back to sign in</button>
        )}
      </form>
    </div>
  );
}
